import React, { useEffect } from "react";
import { Navigate, useLocation, Route } from "react-router-dom";
import { setAuthorization } from "../api/httpClient";
import { useDispatch, useSelector } from "react-redux";
import { useProfile } from "../Components/Hooks/UserHooks";
import { logoutUser } from "../slices/auth/login/thunk";
import { getCompanyById } from "../slices/companies/thunk";
import { hydrateAssignmentsFromToken } from "../slices/assignments";

// Helper para decodificar el token JWT (siguiendo el patrón existente)
const decodeJwtPayload = (token: string) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        return null;
    }
};

const AuthProtected = (props: any) => {
  const dispatch: any = useDispatch();
  const { userProfile, loading, token } = useProfile();
  const location = useLocation();

  // Obtener datos del estado de Redux siguiendo el patrón existente
  const selectedCompany = useSelector((state: any) => state.Companies?.selectedCompany);
  const isCompaniesLoading = useSelector((state: any) => state.Companies?.isLoading);

  // Verificación directa del localStorage para evitar parpadeos o estados intermedios
  const authUser = localStorage.getItem("authUser");
  const userToken = authUser ? JSON.parse(authUser).token : null;

  // Decodificar token para obtener información del usuario
  const decodedToken = userToken ? decodeJwtPayload(userToken) : null;
  const userRoles = decodedToken?.roles?.map((role: any) => role.name) || [];
  const isSuperAdmin = userRoles.includes('Super Admin');
  const isAdmin = userRoles.includes('Admin') || isSuperAdmin;
  const isUser = userRoles.includes('User') && !isAdmin;
  const companyId = decodedToken?.companyId;

  useEffect(() => {
    if (userProfile && !loading && token) {
      setAuthorization(token);
      // Hidratar asignaciones actuales desde el token
      dispatch(hydrateAssignmentsFromToken() as any);
    } else if (!userProfile && !loading && !token) {
       // Si no hay perfil ni token y ya no está cargando, aseguramos el logout
       dispatch(logoutUser());
    }
  }, [token, userProfile, loading, dispatch]);

  // Cargar compañía del usuario solo una vez si tiene companyId (admins y usuarios regulares)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (companyId && !selectedCompany && !isCompaniesLoading) {
      dispatch(getCompanyById(companyId));
    }
  }, [companyId, dispatch, isCompaniesLoading, selectedCompany]);

  // Si no hay token en localStorage, redirigir al login inmediatamente
  if (!userToken) {
    return (
      <Navigate to={{ pathname: "/login" }} state={{ from: location }} replace />
    );
  }

  // Restricción para usuarios User: pueden acceder a Dashboard y rutas de facturación si tienen compañía
  if (isUser) {
    const allowedRoutes = ['/dashboard', '/billing', '/billing-list', '/remission-guide', '/remission-guide-list'];
    const isAllowedRoute = allowedRoutes.includes(location.pathname);
    if (!isAllowedRoute) {
      return (
        <Navigate to={{ pathname: "/dashboard" }} state={{ from: location }} replace />
      );
    }
  }

  // Restricción para Admin sin compañía: solo pueden acceder al dashboard (excepto Super Admin)
  if (isAdmin && !isSuperAdmin && !companyId) {
    const isDashboardRoute = location.pathname === '/dashboard';
    if (!isDashboardRoute) {
      return (
        <Navigate to={{ pathname: "/dashboard" }} state={{ from: location }} replace />
      );
    }
  }

  return <>{props.children}</>;
};

const AccessRoute = ({ component: Component, ...rest }: any) => {
  return (
    <Route
      {...rest}
      render={(props: any) => {
        return (<> <Component {...props} /> </>);
      }}
    />
  );
};

export { AuthProtected, AccessRoute };
